import React, { useState, useRef } from "react";
import { RCCDropDownItem, RCCGrid, RCCIcon, RCCInput } from "react-common-components";
import { useForm } from "react-hook-form";
import Loader from "src/components/Loader";
import SidebarCard from "src/components/SidebarCard";
import { IRefSidebarCard, ISidebarCardItem } from "src/components/SidebarCard/interface";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import SidebarCardButtons from "src/components/SidebarCard/sidebarCardButtons";
import formatDate from "src/utils/formatDate";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { useParams } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import Select from "src/components/Select";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { IBudgetPlan, IBudgetPlanSelected, IFinancialManagementFilters } from "./interface";
import PresentationGroupContent from "./components/PresentationContent";
import ModalsBudgetPlanAddChangeDelete from "./components/Modals/modalAddChangeDelete";
import {IRefBudgetPlanModals, IRefBudgetPlanModalsCopy} from "./components/Modals/interface";
import ModalsCopy from "./components/Modals/modalCopy";

const FinancialManagementPanel: React.FC = () => {
	const params = useParams<{
        budgetPlanId: string;
    }>();
	const { translate, currentLanguage } = useTranslation();
	const { administrator, superUser, checkIfHasSpecificPermission } = useAuth();

	const sidebarRef = useRef<IRefSidebarCard>(null);

	const [budgetPlans, setBudgetPlans] = useState<ISidebarCardItem[]>([]);	
	const [budgetPlanSelected, setBudgetPlanSelected] = useState<IBudgetPlanSelected | undefined>(undefined);

	const modalsBudgetPlanRef = useRef<IRefBudgetPlanModals>(null);
	const modalsBudgetPlanCopyRef = useRef<IRefBudgetPlanModalsCopy>(null);

	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFinancialManagementFilters>({filter:"", draft: "T", startDate: null, endDate: null});
	const { handleSubmit : handleSubmitFilters, register : registerFilters, control: controlFilters, reset: resetFilters, setValue: setValueFilters } = useForm();
	const submitFilters = (data: any) => {
		setBudgetPlanSelected(undefined);
		setFilters({
			...data,
			draft: data.draft.value
		});
	};
    
	const { loading } = useEffectFetchData<IBudgetPlan[]> (
		"FinancialManagement/ListBudgetPlans", 
		{ 
			...filters, 
			startDate: filters.startDate ? formatDate(filters.startDate) : "",
			endDate: filters.endDate ? formatDate(filters.endDate) : ""
		 },
		 ({ data }) => {
			setBudgetPlans(data.map((card) => ({
				id: card.ofa001Id,
				title: card.ofa001Nome,
				subtitle: `${formatDate(card.ofa001Dtinicio, currentLanguage)} ${translate("até")} ${formatDate(card.ofa001Dtfim, currentLanguage)}`,
				filtered: true,
				selected: false,
				favorite: false,
				active:  card.ofa001Rascunho === "N",
				permission: card.permission,
				corporativeValuesUpdated: card.ofa001Vlrcorpatu === "S",
				startDate: card.ofa001Dtinicio,
				endDate: card.ofa001Dtfim
			})));

			if(params.budgetPlanId && params.budgetPlanId.length > 0){
				const cardSelected = data.find((x) => x.ofa001Id === Number(params.budgetPlanId));
				if(cardSelected)
					setBudgetPlanSelected({
						id: Number(params.budgetPlanId),
						title: cardSelected?.ofa001Nome || "",
						corporativeValuesUpdated: cardSelected?.ofa001Vlrcorpatu === "S",
						draft: cardSelected?.ofa001Rascunho === "S",
						startDate: cardSelected.ofa001Dtinicio,
						endDate: cardSelected.ofa001Dtfim	
					});
			}        
		 },
		 [filters]
	);

	if(loading)
		return <Loader />;

	return (
		<>
			<SidebarCard 
				ref={sidebarRef}
				title={translate("Plano orçamentário")} 
				trashLink="oftba001"
				icon={() => <AppIcons name="financialManagement" />}
				defaultCardSelected={params.budgetPlanId && params.budgetPlanId.length > 0 ? Number(params.budgetPlanId) : undefined}
				options={(card: ISidebarCardItem) => administrator || superUser || card.permission === "1" ? (
					<>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCDropDownItem
								selfEnclose
								onClick={ (e) => {e.stopPropagation(); modalsBudgetPlanRef.current?.actionButtonChange(card); } }
							>
								<RCCIcon name="pencil" 
								/>
								{translate("Alterar")}
							</RCCDropDownItem>
							<RCCDropDownItem
								selfEnclose
								onClick={ (e) => {e.stopPropagation(); modalsBudgetPlanCopyRef.current?.actionButtonCopy(card); } }
							>
								<RCCIcon name="copy" 
								/>
								{translate("Copiar")}
							</RCCDropDownItem>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCDropDownItem 
									selfEnclose
									onClick={(e) => {e.stopPropagation(); modalsBudgetPlanRef.current?.actionButtonDelete(card);}}
								>
									<RCCIcon name="trash" 
									/>
									{translate("Excluir")}
								</RCCDropDownItem>
							</CheckPermission>
						</CheckPermission>
				  </>
				) : null}
				content={<PresentationGroupContent 
					selectedBudgetPlan={budgetPlanSelected}
				/>}
				cardClick={async (id, title, card) => {
					const budgetSelect = budgetPlans.find((e) => e.id === id);
					if(budgetSelect)
						setBudgetPlanSelected({
							id,
							title: title || "",
							corporativeValuesUpdated: card.corporativeValuesUpdated,
							draft: !card.active,
							startDate: budgetSelect.startDate,
							endDate: budgetSelect.endDate
						});
					window.history.replaceState(null, "", `${"/financialmanagement/panel"}/${id}`);
				}}
				cards={budgetPlans}
				filterContent={<FiltersContainer
					resetFilters={() => resetFilters({filter:"", draft: {value: "T", label: translate("Todos")} , startDate: null, endDate: null})}
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					showFilters={showFilters}
				>
					<RCCGrid
						container 
						item
						xs={12}
					>
						<DateTimePickerRange 
							control={controlFilters}
							type="datePicker"
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit="startDate"
							nameEnd="endDate"
							setValue={setValueFilters}
							defaultValueEnd={filters.endDate}
							defaultValueInit={filters.startDate}
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
					>
						<Select 
							control={controlFilters}
							name="draft" 
							options={[
								{value: "S", label: translate("Sim")}, 
								{value: "N", label: translate("Não")},
								{value: "T", label: translate("Todos")}
							]} 
							label={translate("Rascunho")}
							defaultValue={{value: "T", label: translate("Todos")}}
							submit="value and label"
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						styleInline="margin-bottom: 30px !important"
					>
						<RCCInput
							name="filter" 
							label={translate("Pesquisar")}
							register={registerFilters}
							defaultValue={filters.filter}
						/>
					</RCCGrid>
				</FiltersContainer>}
				buttonsContent={
					<SidebarCardButtons 
						labelButtonPlus={translate("Novo plano orçamentário")}
						onClickPlus={() => modalsBudgetPlanRef.current?.actionButtonAdd()}
						colorFiltersButton={showFilters || filters.filter || (filters.startDate !== null && filters.endDate !== null) || filters.draft !== "T" ? "blue" : "grey"}
						onClickFilters={() => setShowFilters((prevState) => !prevState)}
						hasPermission={checkIfHasSpecificPermission("financialmanagement/panel", PermissionEnum.CREATE_UPDATE)}
					/>
				}
			/>
			<ModalsBudgetPlanAddChangeDelete 
				ref={modalsBudgetPlanRef} 
				onCallbackAdd={(data: IBudgetPlan) => {
					setBudgetPlans((prevState) => [...prevState, {
						id: data.ofa001Id,
						title: data.ofa001Nome,
						subtitle: `${formatDate(data.ofa001Dtinicio, currentLanguage)} ${translate("até")} ${formatDate(data.ofa001Dtfim, currentLanguage)}`,
						filtered: true,
						selected: false,
						favorite: false,
						active:  data.ofa001Rascunho === "N",
						endDate: data.ofa001Dtfim,
						startDate: data.ofa001Dtinicio	
					}]);
					sidebarRef.current?.onClickCardFuncion(data.ofa001Id, data.ofa001Nome, {
						id: data.ofa001Id,
						title: data.ofa001Nome,
						active: data.ofa001Rascunho === "N",
						corporativeValuesUpdated: true
					});
					sidebarRef.current?.changeListVisibility();
				}}
				onCallbackDelete={(id: number) => {
					setBudgetPlans((prevState) => prevState.filter((x) => x.id !== id));
					setBudgetPlanSelected(undefined);
				}}
				onCallbackUpdate={(data: IBudgetPlan) => {
					let previousValuesCorporativeValues = true;
					setBudgetPlans((prevState) => prevState.map((pv) => {
						if(pv.id !== data.ofa001Id)
							return pv;
						previousValuesCorporativeValues = pv.corporativeValuesUpdated;
						return {
							id: data.ofa001Id,
							title: data.ofa001Nome,
							subtitle: `${formatDate(data.ofa001Dtinicio, currentLanguage)} ${translate("até")} ${formatDate(data.ofa001Dtfim, currentLanguage)}`,
							filtered: true,
							selected: false,
							favorite: false,
							active:  data.ofa001Rascunho === "N",
							permission: data.permission,
							corporativeValuesUpdated: pv.corporativeValuesUpdated,
							endDate: data.ofa001Dtfim,
							startDate: data.ofa001Dtinicio
						};
					}));
					sidebarRef.current?.onClickCardFuncion(data.ofa001Id, data.ofa001Nome, {
						id: data.ofa001Id,
						title: data.ofa001Nome,
						active: data.ofa001Rascunho === "N",
						corporativeValuesUpdated: previousValuesCorporativeValues
					});
					sidebarRef.current?.changeListVisibility();
				}}
			/>
			<ModalsCopy 
				ref={modalsBudgetPlanCopyRef}
				onCallback={(data: IBudgetPlan) => {
					setBudgetPlans((prevState) => [...prevState, {
						id: data.ofa001Id,
						title: data.ofa001Nome,
						subtitle: `${formatDate(data.ofa001Dtinicio, currentLanguage)} ${translate("até")} ${formatDate(data.ofa001Dtfim, currentLanguage)}`,
						filtered: true,
						selected: false,
						favorite: false,
						active:  data.ofa001Rascunho === "N",
						permission: data.permission,
						corporativeValuesUpdated: true,
						endDate: data.ofa001Dtfim,
						startDate: data.ofa001Dtinicio
					}]);
					sidebarRef.current?.onClickCardFuncion(data.ofa001Id, data.ofa001Nome, {
						id: data.ofa001Id,
						title: data.ofa001Nome,
						active: data.ofa001Rascunho === "N",
						corporativeValuesUpdated: true
					});
					sidebarRef.current?.changeListVisibility();
				}}
			/>
		</>
	);
};

export default FinancialManagementPanel;
